// src/services/clientService.js
import { 
    collection, 
    doc, 
    getDocs, 
    setDoc, 
    deleteDoc, 
    query, 
    orderBy, 
    serverTimestamp,
    where,
    writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';

const COLLECTION = 'clients';

export const clientService = {
    /**
     * Obtiene todos los clientes.
     * Nota: El ordenamiento especial (PENDIENTE primero) se hace en el frontend
     * para no complicar los índices compuestos de Firestore innecesariamente.
     */
    getAll: async () => {
        try {
            const q = query(collection(db, COLLECTION), orderBy('nombre'));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error("Error al obtener clientes:", error);
            throw error;
        }
    },

    /**
     * Guarda un cliente. Maneja la lógica crítica de MIGRACIÓN de ID.
     * Si viene oldIdToMigrate, crea el nuevo con RUT y borra el viejo.
     */
    saveClient: async (clientData, oldIdToMigrate = null) => {
        try {
            // Asegurar timestamps
            const dataToSave = {
                ...clientData,
                updatedAt: serverTimestamp()
            };
            if (!dataToSave.createdAt) {
                dataToSave.createdAt = serverTimestamp();
            }

            // CASO MIGRACIÓN (Ej: De 'PENDIENTE_XYZ' a '76123456K')
            if (oldIdToMigrate) {
                const batch = writeBatch(db);
                
                // 1. Crear documento nuevo con el ID correcto (RUT)
                const newDocRef = doc(db, COLLECTION, clientData.rut);
                batch.set(newDocRef, dataToSave);
                
                // 2. Borrar documento viejo
                const oldDocRef = doc(db, COLLECTION, oldIdToMigrate);
                batch.delete(oldDocRef);

                await batch.commit();
                return { isMigration: true, newId: clientData.rut };
            } 
            
            // CASO NORMAL (Crear o Editar sin cambiar ID)
            else {
                // El ID del documento es el RUT (o el ID que venga en clientData.rut si es nuevo)
                const targetId = clientData.rut; 
                await setDoc(doc(db, COLLECTION, targetId), dataToSave, { merge: true });
                return { isMigration: false, newId: targetId };
            }
        } catch (error) {
            console.error("Error al guardar cliente:", error);
            throw error;
        }
    },

    /**
     * Elimina un cliente.
     */
    delete: async (id) => {
        try {
            await deleteDoc(doc(db, COLLECTION, id));
        } catch (error) {
            console.error("Error al eliminar cliente:", error);
            throw error;
        }
    },

    /**
     * Obtiene el historial (Visitas o Cotizaciones) de un cliente.
     */
    getHistory: async (clientId, type) => {
        try {
            // type debe ser 'visits' o 'quotes'
            const collectionName = type === 'visits' ? 'technicalReports' : 'quotes';
            const q = query(collection(db, collectionName), where('clientId', '==', clientId));
            const snapshot = await getDocs(q);
            
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Ordenamiento por fecha descendente (más reciente primero)
            data.sort((a, b) => {
                const dateA = a.createdAt?.seconds || a.fecha?.seconds || 0;
                const dateB = b.createdAt?.seconds || b.fecha?.seconds || 0;
                return dateB - dateA; 
            });

            return data;
        } catch (error) {
            console.error(`Error obteniendo historial de ${type}:`, error);
            return [];
        }
    },

    /**
     * Procesa la importación masiva desde CSV.
     * Recibe el texto crudo del archivo y ejecuta el Batch Write.
     */
    importFromCSV: async (csvText) => {
        try {
            const lines = csvText.split('\n');
            const batch = writeBatch(db);
            let count = 0;

            for (let i = 1; i < lines.length; i++) {
                const line = lines[i].trim();
                if (!line) continue;

                const cols = line.split(';');
                if (cols.length < 2) continue; 

                const rawRut = cols[0] || '';
                const cleanRut = rawRut.replace(/[.-]/g, '').trim();
                if (!cleanRut) continue;

                const clientData = {
                    rut: cleanRut,
                    nombre: (cols[1] || '').trim(),
                    telefono: (cols[2] || '').replace(/\s/g, ''),
                    email: (cols[3] || '').trim(),
                    direccion: (cols[4] || '').trim(),
                    mainName: (cols[5] || '').trim(), mainPhone: (cols[6] || '').trim(), mainEmail: (cols[7] || '').trim(),
                    paymentName: (cols[8] || '').trim(), paymentPhone: (cols[9] || '').trim(), paymentEmail: (cols[10] || '').trim(),
                    adminName: (cols[11] || '').trim(), adminPhone: (cols[12] || '').trim(), adminEmail: (cols[13] || '').trim(),
                    conciergeName: (cols[14] || '').trim(), conciergePhone: (cols[15] || '').trim(), conciergeEmail: (cols[16] || '').trim(),
                    opsName: (cols[17] || '').trim(), opsPhone: (cols[18] || '').trim(), opsEmail: (cols[19] || '').trim(),
                    notes: (cols[20] || '').trim(),
                    updatedAt: serverTimestamp()
                };
                
                const clientRef = doc(db, COLLECTION, cleanRut);
                batch.set(clientRef, clientData, { merge: true });
                count++;
            }

            if (count > 0) {
                await batch.commit();
            }
            return count;

        } catch (error) {
            console.error("Error en importFromCSV:", error);
            throw error;
        }
    }
};